/**
 * useSystemSizes - Hook for calculating system sizes
 *
 * Calculates the sizes of PV, battery, and generator systems.
 * Prefers optimization results when available, falls back to form inputs.
 *
 * @module hooks/useSystemSizes
 */

import { useMemo } from 'react';
import { debug } from '../utils/debug';

/**
 * Calculate system sizes from optimization results or form inputs
 *
 * @param {Object} options
 * @param {Object} options.optimization - Optimization state with results
 * @param {Object} options.scenarioForm - Form state with system configuration
 * @param {Object} options.loadProfile - Load profile state with existing solar params
 * @returns {Object} System sizes { pvKw, batteryKw, batteryKwh, generatorKw }
 */
export function useSystemSizes({ optimization, scenarioForm, loadProfile }) {
    return useMemo(() => {
        // Check if we have optimization results with actual sizes
        // Include CHP (natural gas generators) in addition to Generator (diesel)
        const outputs = optimization?.results?.outputs;
        const hasResults = outputs && (outputs.PV || outputs.ElectricStorage || outputs.Generator || outputs.CHP);

        if (hasResults) {
            // Use actual sizes from optimization results
            // REopt returns total PV including existing - subtract existing for budget purposes
            const totalPvKw = outputs.PV?.size_kw || 0;
            const existingSolarKw = parseFloat(loadProfile?.existingSolarParams?.system_capacity) || 0;
            const pvKw = Math.max(0, totalPvKw - existingSolarKw);

            const batteryKw = outputs.ElectricStorage?.size_kw || 0;
            const batteryKwh = outputs.ElectricStorage?.size_kwh || 0;
            // Generator capacity = diesel (Generator) + natural gas (CHP)
            const generatorKw = (outputs.Generator?.size_kw || 0) + (outputs.CHP?.size_kw || 0);

            debug.log('📊 [useSystemSizes] Using optimization results:', {
                totalPvKw, existingSolarKw, pvKw, batteryKw, batteryKwh, generatorKw,
                dieselKw: outputs.Generator?.size_kw, chpKw: outputs.CHP?.size_kw
            });
            return { pvKw, batteryKw, batteryKwh, generatorKw };
        }

        // Fall back to form inputs (for before optimization runs)
        // IMPORTANT: Only include sizes for technologies that are ENABLED
        // This prevents stale values from disabled technologies appearing in budget
        const pvKw = scenarioForm.usePv ? (parseFloat(scenarioForm.pvMaxKw) || 0) : 0;

        // Battery sizes - only if storage is enabled
        // BatteryConfiguration always sets storageMaxKw/storageMaxKwh directly for both modes
        // The batteries array is for multi-battery scenarios (not currently used in the UI)
        let batteryKw = 0;
        let batteryKwh = 0;

        if (scenarioForm.useStorage) {
            batteryKw = parseFloat(scenarioForm.storageMaxKw) || 0;
            batteryKwh = parseFloat(scenarioForm.storageMaxKwh) || 0;

            // Fallback: if storageMax values are empty but batteries array has values, sum them
            if (batteryKw === 0 && batteryKwh === 0 && scenarioForm.batteries?.length > 0) {
                batteryKw = scenarioForm.batteries.reduce((sum, b) => sum + (parseFloat(b.kw) || 0), 0);
                batteryKwh = scenarioForm.batteries.reduce((sum, b) => sum + (parseFloat(b.kwh) || 0), 0);
            }
        }

        // Generator size - only if generator is enabled
        // Support both multi-generator array and legacy single-generator field
        let generatorKw = 0;
        if (scenarioForm.useGenerator) {
            const generators = scenarioForm.generators || [];
            if (generators.length > 0) {
                // Multi-generator: sum all generator capacities
                generatorKw = generators.reduce((sum, g) => sum + (parseFloat(g.kw) || 0), 0);
            } else {
                // Legacy single-generator fallback
                generatorKw = parseFloat(scenarioForm.generatorMaxKw) || 0;
            }
        }

        debug.log('📊 [useSystemSizes] Using form inputs:', { pvKw, batteryKw, batteryKwh, generatorKw });
        return { pvKw, batteryKw, batteryKwh, generatorKw };
    }, [
        optimization?.results?.outputs,
        scenarioForm.usePv,
        scenarioForm.pvMaxKw,
        scenarioForm.useStorage,
        scenarioForm.batteries,
        scenarioForm.storageMaxKw,
        scenarioForm.storageMaxKwh,
        scenarioForm.useGenerator,
        scenarioForm.generatorMaxKw,
        scenarioForm.generators,
        loadProfile?.existingSolarParams
    ]);
}

export default useSystemSizes;
